/**
 * Ce que chaque type de bénéficiaire implique — un seul endroit.
 *
 * Un salarié, un ayant droit et un client externe ne se rattachent pas au
 * référentiel de la même façon, ne se vérifient pas contre le DNS de la même
 * façon, et ne se désactivent pas pour le même motif. Ces différences
 * étaient écrites en `if (type === 'EXTERNE')` dans l'enrôlement, la file,
 * l'éligibilité et le SQL. Elles vivent désormais ici : le jour où une règle
 * change, un seul fichier change, et un test dit ce qui a bougé.
 *
 * Deux notions à ne pas confondre :
 *
 *   - le **client externe** est une PERSONNE sans entreprise (RG-PA-02) ;
 *   - le **paiement immédiat** est le PARCOURS d'une visite non couverte —
 *     celui d'un client externe, mais aussi d'un salarié dont l'entreprise
 *     est suspendue (voir `determinerParcours`).
 */
/** Reprend valeur pour valeur l'enum Prisma `TypeBeneficiaire` (un test API le vérifie). */
export const TYPES_BENEFICIAIRE = ['TITULAIRE', 'CONJOINT', 'ENFANT', 'EXTERNE'];
export const PROFILS_BENEFICIAIRE = {
    // Absent du DNS : c'est exactement ce que dit une infirmation.
    TITULAIRE: {
        rattachement: 'CONTRAT',
        aConfirmer: true,
        motifSiInfirme: 'ABSENT_DU_DERNIER_DNS',
        couvert: true,
    },
    // Un DNS ne nomme jamais un ayant droit (CP-01) : l'infirmer, c'est
    // constater que son salarié ne le reconnaît pas.
    CONJOINT: {
        rattachement: 'TITULAIRE',
        aConfirmer: true,
        motifSiInfirme: 'DESACTIVATION_MANUELLE',
        couvert: true,
    },
    ENFANT: {
        rattachement: 'TITULAIRE',
        aConfirmer: true,
        motifSiInfirme: 'DESACTIVATION_MANUELLE',
        couvert: true,
    },
    EXTERNE: { rattachement: 'AUCUN', aConfirmer: false, motifSiInfirme: null, couvert: false },
};
export function profilDe(type) {
    return PROFILS_BENEFICIAIRE[type];
}
export function estClientExterne(type) {
    return !profilDe(type).couvert;
}
/** Les types qui entrent dans la file « à confirmer » — pour le SQL de la file. */
export const TYPES_A_CONFIRMER = TYPES_BENEFICIAIRE.filter((type) => PROFILS_BENEFICIAIRE[type].aConfirmer);
/**
 * L'état d'une fiche à sa création au guichet : à confirmer si un DNS peut
 * la confirmer, confirmée d'emblée sinon — il n'y a rien à vérifier.
 */
export function etatConfirmationInitial(type) {
    return profilDe(type).aConfirmer ? 'A_CONFIRMER' : 'CONFIRME';
}
/**
 * Un client externe qui revient avec la carte de son entreprise devient un
 * salarié : c'est la seule conversion de type admise. Un ayant droit ne
 * devient pas salarié par ce chemin — c'est une autre personne, un autre
 * dossier.
 */
export function peutEtreRattacheAUneEntreprise(type) {
    return profilDe(type).rattachement === 'AUCUN';
}
/**
 * Le lien avec l'assuré, dit dans la file du médecin avec les mots de la
 * Direction (décision 277) : « Agent » et non « Salarié », qui reste le mot
 * de l'accueil ailleurs.
 */
export const LIBELLES_FILE_BENEFICIAIRE = {
    TITULAIRE: 'Agent',
    CONJOINT: 'Conjoint',
    ENFANT: 'Enfant',
    EXTERNE: 'Externe',
};
export function libelleDeFile(type) {
    return LIBELLES_FILE_BENEFICIAIRE[type];
}
export const LETTRES_BENEFICIAIRE = {
    TITULAIRE: 'A',
    CONJOINT: 'C',
    ENFANT: 'E',
    // Un client externe n'a pas d'assuré : X, celle de son numéro de passage (GX1).
    EXTERNE: 'X',
};
export function lettreDe(type) {
    return LETTRES_BENEFICIAIRE[type];
}
//# sourceMappingURL=beneficiaire.js.map